/* eslint-disable jsx-a11y/anchor-is-valid */
"use client";
import { use, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import StoreHeader from "../components/store/store-header";
import StoreFooter from "../components/store/store-footer";
import { ArrowLeft, ArrowRight } from "lucide-react";
// import fs from "fs";
// import path from "path";
// import type { NextApiRequest, NextApiResponse } from "next";

export default function Store() {
    // Switching languages
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const handleChangeLang = (lang: string) => {
        i18n.changeLanguage(lang);
        if (lang === "ar" || lang === "ur") {
            document.documentElement.dir = "rtl";
        } else {
            document.documentElement.dir = "ltr";
        }
    };

    // FAQ toggle state
    const [openFAQ, setOpenFAQ] = useState<number | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Prevent scrolling when mobile menu is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }, [mobileOpen]);

    // Sample FAQ data
    const faqs = [
        {
            question: "Why we choose Zabehaty?",
            answer: "Zabehaty is your trusted sacrificial store providing fresh, halal meat with best quality and service.",
        },
        {
            question: "How we can purchase from Zabehaty?",
            answer: "You can purchase directly through our app or contact us for special orders.",
        },
        {
            question: "Zabehaty only located in UAE?",
            answer: "We deliver across UAE with timely delivery options available.",
        },
        {
            question: "How we can purchase from Zabehaty?",
            answer: "You can purchase directly through our app or contact us for special orders.",
        },
        {
            question: "How we can purchase from Zabehaty?",
            answer: "You can purchase directly through our app or contact us for special orders.",
        },
    ];
    // Arabic FAQ
    const faqs_ar = [
        {
            question: "لماذا نختار زبيحتي؟",
            answer: "زبيحتي هو متجرك الموثوق للأضاحي يقدم لحومًا طازجة وحلال بأفضل جودة وخدمة.",
        },
        {
            question: "كيف يمكننا الشراء من زبيحتي؟",
            answer: "يمكنك الشراء مباشرة من خلال تطبيقنا أو التواصل معنا للطلبات الخاصة.",
        },
        {
            question: "هل زبيحتي موجود فقط في الإمارات؟",
            answer: "نقوم بالتوصيل في جميع أنحاء الإمارات مع خيارات توصيل في الوقت المحدد.",
        },
        {
            question: "كيف يمكننا الشراء من زبيحتي؟",
            answer: "يمكنك الشراء مباشرة من خلال تطبيقنا أو التواصل معنا للطلبات الخاصة.",
        },
        {
            question: "كيف يمكننا الشراء من زبيحتي؟",
            answer: "يمكنك الشراء مباشرة من خلال تطبيقنا أو التواصل معنا للطلبات الخاصة.",
        },
    ];

    // Urdu FAQ
    const faqs_ur = [
        {
            question: "ہم زبیحتی کیوں منتخب کرتے ہیں؟",
            answer: "زبیحتی آپ کا معتبر قربانی اسٹور ہے جو تازہ، حلال گوشت بہترین معیار اور خدمات کے ساتھ فراہم کرتا ہے۔",
        },
        {
            question: "ہم زبیحتی سے کیسے خریداری کر سکتے ہیں؟",
            answer: "آپ براہِ راست ہمارے ایپ کے ذریعے خریداری کر سکتے ہیں یا خصوصی آرڈرز کے لیے ہم سے رابطہ کریں۔",
        },
        {
            question: "کیا زبیحتی صرف متحدہ عرب امارات میں ہے؟",
            answer: "ہم متحدہ عرب امارات بھر میں ترسیل کرتے ہیں اور بروقت ترسیل کے اختیارات دستیاب ہیں۔",
        },
        {
            question: "ہم زبیحتی سے کیسے خریداری کر سکتے ہیں؟",
            answer: "آپ براہِ راست ہمارے ایپ کے ذریعے خریداری کر سکتے ہیں یا خصوصی آرڈرز کے لیے ہم سے رابطہ کریں۔",
        },
        {
            question: "ہم زبیحتی سے کیسے خریداری کر سکتے ہیں؟",
            answer: "آپ براہِ راست ہمارے ایپ کے ذریعے خریداری کر سکتے ہیں یا خصوصی آرڈرز کے لیے ہم سے رابطہ کریں۔",
        },
    ];

    // Trusted partners logos
    const scrollRef = useRef<HTMLDivElement>(null);
    const partners = [
        {
            link: "https://www.dmt.gov.ae/en/adm",
            logo: "/store/trusted_partners/trusted_partner1.svg",
        },
        {
            link: "https://shjc.sharjah.ae/",
            logo: "/store/trusted_partners/trusted_partner2.svg",
        },
        {
            link: "https://www.daralber.ae/en/home",
            logo: "/store/trusted_partners/trusted_partner3.svg",
        },
        {
            link: "https://ssef.shj.ae/",
            logo: "/store/trusted_partners/trusted_partner4.svg",
        },
        {
            link: "https://www.hifzalnaema.com/en/",
            logo: "/store/trusted_partners/trusted_partner5.svg",
        },
        {
            link: "https://www.dm.gov.ae/%D8%A8%D9%86%D9%83-%D8%A7%D9%84%D8%A5%D9%85%D8%A7%D8%B1%D8%A7%D8%AA-%D9%84%D9%84%D8%B7%D8%B9%D8%A7%D9%85/?lang=ar",
            logo: "/store/trusted_partners/trusted_partner6.svg",
        },
    ];
    const containerRef = useRef<HTMLDivElement>(null);
    const partnerScroll = (direction: "left" | "right") => {
        if (!scrollRef.current) return;
        const container = scrollRef.current;
        const scrollAmount = container.clientWidth / 4;
        if (direction === "left") {
            container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
            container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    // Stores logos
    const backgrounds = [
        "/store/shop_backgrounds/meat.jpg",
        "/store/shop_backgrounds/chicken.jpg",
        "/store/shop_backgrounds/fish.jpg",
        "/store/shop_backgrounds/vegetables.jpg",
    ];

    const getBackgroundForLogo = (logo: string) => {
        const match = logo.match(/_(\w+)\./);
        const keyword = match ? match[1].toLowerCase() : "";
        const bg = backgrounds.find((bg) => bg.toLowerCase().includes(keyword));
        return bg || backgrounds[0];
    };

    const shops = [
        "/store/shop_logos/logo1_meat.png",
        "/store/shop_logos/logo2_vegetables.png",
        "/store/shop_logos/logo3_fish.png",
        "/store/shop_logos/logo4_chicken.png",
        "/store/shop_logos/logo5_fish.jpg",
        "/store/shop_logos/logo6_vegetables.png",
        "/store/shop_logos/logo7_vegetables.jpg",
        "/store/shop_logos/logo8_vegetables.jpg",
        "/store/shop_logos/logo9_chicken.jpg",
        "/store/shop_logos/logo10_chicken.jpg",
        "/store/shop_logos/logo11_chicken.png",
        "/store/shop_logos/logo12_chicken.jpg",
        "/store/shop_logos/logo13_fish.jpg",
        "/store/shop_logos/logo14_vegetables.jpg",
        "/store/shop_logos/logo15_vegetables.jpg",
        "/store/shop_logos/logo16_vegetables.jpg",
        "/store/shop_logos/logo17_chicken.png",
        "/store/shop_logos/logo18_vegetables.jpg",
        "/store/shop_logos/logo19_meat.png",
        "/store/shop_logos/logo20_meat.jpg",
        "/store/shop_logos/logo21_meat.png",
        "/store/shop_logos/logo22_meat.jpg",
        "/store/shop_logos/logo23_meat.png",
        "/store/shop_logos/logo24_meat.jpg",
        "/store/shop_logos/logo25_vegetables.png",
        "/store/shop_logos/logo26_meat.jpg",
        "/store/shop_logos/logo27_meat.png",
        "/store/shop_logos/logo28_meat.jpg",
        "/store/shop_logos/logo29_meat.jpg",
        "/store/shop_logos/logo30_meat.jpg",
        "/store/shop_logos/logo31_meat.jpg",
        "/store/shop_logos/logo32_meat.jpg",
        "/store/shop_logos/logo33_meat.jpg",
        "/store/shop_logos/logo34_meat.jpg",
        "/store/shop_logos/logo35_meat.jpg",
        "/store/shop_logos/logo36_meat.jpg",
        "/store/shop_logos/logo37_meat.png",
        "/store/shop_logos/logo38_vegetables.png",
        "/store/shop_logos/logo39_meat.jpg",
        "/store/shop_logos/logo40_vegetables.jpg",
    ];

    const shopScrollRef = useRef<HTMLDivElement>(null);

    const shopScroll = (direction: "left" | "right") => {
        const container = shopScrollRef.current;
        if (!container) return;

        const scrollAmount = container.offsetWidth; // scroll one full view width (4 items)
        if (direction === "left") {
            container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
        } else {
            container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        }
    };

    // Video Reviews
    const coverImages = [
        "/store/video_reviews/video_review1.png",
        "/store/video_reviews/video_review2.png",
        "/store/video_reviews/video_review3.png",
    ];
    const [videos, setVideos] = useState<
        { videoId: string; title: string; thumbnail: string }[]
    >([]);
    const [startIndex, setStartIndex] = useState(0);
    const [activeVideo, setActiveVideo] = useState<string | null>(null);
    const visibleCount = 3;

    const playlistId = "PLbcFr97L4NnbAmozEfbcj9AshaE-wzpKR";
    const apiKey = "AIzaSyBJmg_TLI-L0rGuCvRe4-doi9bC5cahqbI";

    useEffect(() => {
        const fetchPlaylist = async () => {
            const res = await fetch(
                `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${playlistId}&key=${apiKey}`,
            );
            const data = await res.json();

            const items = data.items.map((item: any, index: number) => ({
                videoId: item.snippet.resourceId.videoId,
                title: item.snippet.title,
                thumbnail: coverImages[index % coverImages.length],
            }));
            setVideos(items);
        };
        fetchPlaylist();
    }, [playlistId]);

    const handlePrev = () => {
        setStartIndex((prev) => Math.max(prev - visibleCount, 0));
    };

    const handleNext = () => {
        setStartIndex((prev) =>
            Math.min(prev + visibleCount, videos.length - visibleCount),
        );
    };

    // Customer reviews
    const baseCustomers = [
        {
            id: 1,
            name: "Zahra Saeed",
            photo: "landingpage_customer1.jpg",
            rating: 5,
            review: "I love Zabehaty Application this application ia so good. I would use no other Application.",
        },
        {
            id: 2,
            name: "Muhammad Irfan",
            photo: "landingpage_customer2.jpg",
            rating: 5,
            review: "Great customer service and very helpful when I've contacted them.",
        },
    ];
    const arabicCustomers = [
        {
            id: 1,
            name: "سوبر فرونيس",
            photo: "landingpage_customer3.png",
            rating: 5,
            review: `من يوم ما دخلنا مع تطبيق ذبيحتي والأمور فعلاً اتغيرت للأفضل.
منتجاتنا صارت توصل لناس ما كناش متخيلين إنهم يعرفونا أصلًا.
سهولة التطبيق وسمعته الممتازة خلّت عملاء كتيد يثقوا فينا ويشتروا خضارنا الطازة بشكل دوري.
إحنا مبسوطين جدًا من التجربة دي، ونشكر فريق ذبيحتي على دعمهم، وإن شاء اللّه دايعًا نكون مع `,
        },
        {
            id: 2,
            name: "نبع الأصالة للتراث",
            photo: "landingpage_customer4.png",
            rating: 5,
            review: `بصراحة نحن وايد معتنيين بانضعامنا مع تطبيق ذبيحتي، التطبيق هذا فعلاً سهل علينا وايد أمور وخلانا نوصل لبيوت أكثر ونعرّف الناس على شغلنا اليدوي.
المنتجات اللي نسويها بيدنا من البيت خذت صدى طيب والحمدللّه، والفضل يرجع بعد اللّه لتطبيق ذبيحتي اللي عطاناثقة أكبر عند الزباين.
التعاون وياهم عطانا رافع إننا نطور أكثر ونقدّم أشياء جديدة، وإن شاء اللّه الشراكة هاي تكبر وتستعر بالخير والبركة.`,
        },
        {
            id: 3,
            name: "ملحمة العراب",
            photo: "landingpage_customer5.png",
            rating: 5,
            review: `من خلال تعاوننا مع تطبيق ذبيحتي لعسنا فرقًا حقيقيّا في مستونىٰ مبيعاتنا وانتشارنا في السوق.
التطبيق وفد لنا منصة موثوقة وسهلة الاستخدام، مكّنتنا من الوصول لعدد أكبر من العملاء بشكل منظم وسديع.
ما وجدناه من دعم ومتابعة مستعدة من فريق ذبيحتي ساعدنا علىٰ تحقيق نتائج ملعوسة نعتز بها.
ونحن`,
        },
    ];

    const reviews = [
        "/store/customer_reviews/customerreview_1.svg",
        "/store/customer_reviews/customerreview_2.svg",
        "/store/customer_reviews/customerreview_3.svg",
    ];

    const customers = i18n.language === "ar" ? arabicCustomers : baseCustomers;

    // Customer Trust stats
    const trustStats = [
        { label: t("downloads"), value: "100k+" },
        { label: t("reviews"), value: "4.4k+" },
        { label: t("customers"), value: "200k+" },
    ];

    return (
        <>
            {" "}
            <Helmet>
                <title>Zabehaty - Fresh Halal Meat Store</title>
                <meta
                    name="description"
                    content="Zabehaty offers fresh, halal meat with excellent quality and trusted service in UAE."
                />
                <meta
                    name="keywords"
                    content="Halal meat, fresh meat, Zabehaty, meat store, UAE, sacrificial meat"
                />
                <meta name="author" content="Zabehaty" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <link rel="canonical" href="https://zabehaty.com/store" />

                {/* Open Graph / Facebook */}
                <meta
                    property="og:title"
                    content="Zabehaty - Store (Portfolio)"
                />
                <meta
                    property="og:description"
                    content="Zabehaty offers fresh, halal meat with excellent quality and trusted service in UAE."
                />
                <meta
                    property="og:image"
                    content="https://zabehaty.com/landingpage_banner1.jpg"
                />
                <meta property="og:url" content="https://zabehaty.com/store" />
                <meta property="og:type" content="website" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Zabehaty - Fresh Halal Meat Store"
                />
                <meta
                    name="twitter:description"
                    content="Zabehaty offers fresh, halal meat with excellent quality and trusted service in UAE."
                />
                <meta
                    name="twitter:image"
                    content="https://zabehaty.com/landingpage_banner1.jpg"
                />
                <meta name="twitter:site" content="@YourTwitterHandle" />
            </Helmet>
            <StoreHeader />
            <div className="bg-white font-sans antialiased">
                {/* About Us */}
                <section className="mx-auto max-w-4xl px-6 py-16 text-center">
                    <h2 className="mb-6 text-3xl">
                        <p className="font-regular">
                            <span className="font-bold">
                                {t("about-us").split(" ")[0]}
                            </span>{" "}
                            {t("about-us").split(" ").slice(1).join(" ")}
                        </p>
                    </h2>
                    <p className="mb-6 leading-relaxed text-gray-700">
                        {t("about-us-desc")}
                    </p>
                </section>

                {/* Trusted Partners */}
                <section
                    className="relative py-12"
                    style={{
                        backgroundImage: `url('/store/trusted_partners/background_image.jpg')`,
                        minHeight: "600px",
                        padding: "2rem",
                        width: "100%",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                >
                    <div className="absolute inset-0 bg-[#211B11] opacity-[70%]"></div>
                    <div className="relative z-10">
                        <h2 className="font-regular mb-6 text-center text-3xl text-white">
                            {(() => {
                                const text = t("our-trusted-partners");
                                const words = text.split(" ");
                                return (
                                    <>
                                        {words.slice(0, -1).join(" ")}{" "}
                                        <span className="font-bold">
                                            {words[words.length - 1]}
                                        </span>
                                    </>
                                );
                            })()}
                        </h2>
                        <h5 className="mx-auto mb-6 w-[90%] text-center text-base text-lg text-white sm:w-[80%]">
                            {t("our-trusted-partners-desc")}
                        </h5>

                        <div className="w-full overflow-hidden px-4 pt-4 sm:px-10 lg:px-20">
                            {/* Scrollable container */}
                            <div
                                ref={scrollRef}
                                className="flex gap-[1px] overflow-x-auto"
                                style={{
                                    msOverflowStyle: "none", // IE and Edge
                                    scrollbarWidth: "none", // Firefox
                                }}
                            >
                                <style>
                                    {`
      div::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
      }
    `}
                                </style>

                                {partners.map((partner, i) => (
                                    <a
                                        key={i}
                                        href={partner.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex h-40 w-1/2 flex-shrink-0 items-center justify-center bg-white opacity-90 transition hover:opacity-80 sm:h-48 sm:w-1/3 md:h-56 md:w-1/4 lg:h-[18rem]"
                                    >
                                        <img
                                            src={partner.logo as string}
                                            alt={`Partner ${i + 1}`}
                                            className="max-h-full max-w-full object-contain transition-transform hover:scale-110"
                                        />
                                    </a>
                                ))}
                            </div>

                            {/* Navigation arrows */}
                            <div className="mb-8 mt-10 flex justify-center gap-4 sm:mb-12 sm:mt-16 sm:gap-6">
                                <button
                                    onClick={() => partnerScroll("left")}
                                    className="bg-white/40 px-3 py-2 text-white transition hover:bg-green-500 sm:py-4"
                                >
                                    <svg
                                        className="scale-x-[-1]"
                                        width="24"
                                        height="10"
                                        viewBox="0 0 24 10"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => partnerScroll("right")}
                                    className="bg-white/40 px-3 py-2 text-white transition hover:bg-green-500 sm:py-4"
                                >
                                    <svg
                                        width="24"
                                        height="10"
                                        viewBox="0 0 24 10"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stores */}
                <section className="mx-auto my-16 max-w-7xl px-4 text-center sm:px-6">
                    <h2 className="mb-6 text-center text-2xl sm:text-3xl">
                        <p className="font-regular">
                            <span className="font-bold">
                                {t("stores-in-our-application").split(" ")[0]}
                            </span>{" "}
                            {t("stores-in-our-application")
                                .split(" ")
                                .slice(1)
                                .join(" ")}
                        </p>
                    </h2>

                    <h5 className="mx-auto mb-6 w-[90%] text-center text-base sm:w-[80%]">
                        {t("stores-in-our-application-desc")}
                    </h5>

                    {/* Scrollable container instead of grid */}
                    <div
                        ref={shopScrollRef}
                        className="no-scrollbar flex w-full gap-6 overflow-x-auto scroll-smooth pt-8 sm:gap-8"
                    >
                        {shops.slice(0, 40).map((logo, i) => {
                            const background = getBackgroundForLogo(logo);
                            return (
                                <div
                                    key={i}
                                    className="group relative flex h-48 w-[250px] flex-shrink-0 items-center justify-center overflow-hidden hover:bg-black/20 sm:h-60 md:h-72 lg:h-80 lg:w-[280px]"
                                    style={{
                                        backgroundImage: `url(${background})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                    }}
                                >
                                    <div className="flex items-center justify-center bg-white/90 p-4 transition duration-300 group-hover:scale-110">
                                        <img
                                            src={logo}
                                            alt={`Shop Logo ${i + 1}`}
                                            className="max-h-28 object-contain transition-transform duration-300 sm:max-h-32 md:max-h-36 lg:max-h-40"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation arrows */}
                    <div className="mb-8 mt-10 flex w-full justify-center gap-4 sm:mb-12 sm:mt-16 sm:gap-6">
                        <button
                            onClick={() => shopScroll("left")}
                            className="bg-black/90 px-2 py-2 text-white transition hover:bg-green-500 sm:py-3"
                        >
                            <svg
                                className="scale-x-[-1]"
                                width="24"
                                height="10"
                                viewBox="0 0 24 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            onClick={() => shopScroll("right")}
                            className="bg-black/90 px-2 py-2 text-white transition hover:bg-green-500 sm:py-3"
                        >
                            <svg
                                width="24"
                                height="10"
                                viewBox="0 0 24 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </section>

                {/* Video Reviews */}
                <section className="relative mx-auto max-w-5xl py-16 pb-28">
                    <h2 className="mb-6 text-center text-3xl">
                        <p className="font-regular">
                            <span className="font-bold">
                                {t("video-reviews").split(" ")[0]}
                            </span>{" "}
                            {t("video-reviews").split(" ").slice(1).join(" ")}
                        </p>
                    </h2>
                    <div className="my-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {videos
                            .slice(startIndex, startIndex + visibleCount)
                            .map((video, i) => (
                                <div
                                    key={i}
                                    className="relative h-[18rem] w-full bg-black"
                                >
                                    {activeVideo === video.videoId ? (
                                        <iframe
                                            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
                                            className="h-full w-full"
                                            title={video.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    ) : (
                                        <button
                                            className="relative h-full w-full"
                                            onClick={() =>
                                                setActiveVideo(video.videoId)
                                            }
                                        >
                                            <img
                                                src={video.thumbnail}
                                                alt={video.title}
                                                className="h-full w-full object-cover"
                                            />
                                            {/* Play button overlay */}
                                            {/* <div className="absolute inset-0 flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-16 w-16 text-white"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div> */}
                                        </button>
                                    )}
                                </div>
                            ))}
                    </div>
                    <div className="flex justify-center gap-6">
                        <button
                            onClick={handlePrev}
                            disabled={startIndex === 0}
                            className="bg-black/90 px-2 py-3 text-white transition hover:bg-green-500 disabled:opacity-40"
                        >
                            <svg
                                className="scale-x-[-1]"
                                width="24"
                                height="10"
                                viewBox="0 0 24 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={
                                startIndex + visibleCount >= videos.length
                            }
                            className="bg-black/90 px-2 py-3 text-white transition hover:bg-green-500 disabled:opacity-40"
                        >
                            <svg
                                width="24"
                                height="10"
                                viewBox="0 0 24 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1 5.00506L23 5.00506M23 5.00506L18 9M23 5.00506L18 0.999999"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </section>

                {/* Customer Reviews */}
                <section
                    className="relative mb-28 flex flex-col items-center py-12"
                    style={{
                        minHeight: "400px",
                        padding: "2rem",
                        width: "100%",
                        backgroundImage: `url('/store/customer_reviews/customerreview_background.jpg')`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                >
                    {/* Overlay with blend */}
                    <div className="absolute inset-0 bg-[#fff1dd] opacity-80"></div>

                    <div className="relative z-10 flex w-full max-w-7xl flex-col items-center py-8">
                        <h2 className="mb-6 text-center text-3xl">
                            <p className="font-regular">
                                <span className="font-bold">
                                    {t("customer-reviews").split(" ")[0]}
                                </span>{" "}
                                {t("customer-reviews")
                                    .split(" ")
                                    .slice(1)
                                    .join(" ")}
                            </p>
                        </h2>

                        <div className="mt-4 flex flex-wrap justify-center gap-8">
                            {reviews.map((logo, i) => (
                                <div
                                    key={i}
                                    className="flex items-center justify-center"
                                >
                                    <img
                                        src={logo as string}
                                        alt={`Customer review ${i + 1}`}
                                        className="h-60 w-auto object-contain transition-transform"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Customer Trust */}
                <section
                    className="relative flex flex-col items-center py-12"
                    style={{
                        minHeight: "400px",
                        padding: "2rem",
                        width: "100%",
                        backgroundImage: `url('/landingpage_customertrust.jpg')`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                >
                    <div className="absolute inset-0 bg-[#332715] opacity-60"></div>
                    <div className="relative z-10 flex w-full max-w-5xl flex-col items-center">
                        <h2 className="mb-6 text-center text-3xl">
                            <p className="font-regular text-white">
                                {t("customer-trust").split(" ")[0]}{" "}
                                <span className="font-bold">
                                    {t("customer-trust")
                                        .split(" ")
                                        .slice(1)
                                        .join(" ")}
                                </span>
                            </p>
                        </h2>
                        <h5 className="mx-auto mb-6 w-[80%] text-center text-lg text-white">
                            {t("customer-trust-desc")}
                        </h5>

                        {/* Responsive Grid */}
                        <div className="mx-auto grid w-full grid-cols-1 gap-4 px-28 pt-4 text-center sm:grid-cols-2 lg:grid-cols-3">
                            {trustStats.map(({ label, value }, i) => (
                                <div
                                    key={i}
                                    className="border border-green-500 bg-white/10 px-6 py-8 shadow-lg backdrop-blur-md"
                                >
                                    <div className="break-words text-4xl font-extrabold text-white drop-shadow-sm">
                                        {value}
                                    </div>
                                    <div className="mt-2 uppercase tracking-widest text-white">
                                        {label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Location */}
                {/* <section className="relative w-full px-6 py-12">
                    <div className="relative mt-6 flex h-auto w-full flex-col overflow-hidden rounded lg:h-64 lg:flex-row">
                        <div className="flex flex-col justify-center bg-white bg-opacity-40 p-6 text-black backdrop-blur-sm lg:w-1/3">
                            <h3 className="mb-6 text-center text-3xl">
                                <p className="font-semibold">
                                    {t("our-location")}
                                </p>
                            </h3>
                            <p className="text-lg font-semibold">
                                {t("address")}:
                            </p>
                            <p>
                                Abu Dhabi City – Al Salam Street “Al Firdous
                                Tower” Abu Dhabi, UAE
                            </p>

                            <button
                                className="mt-6 w-max rounded bg-green-600 px-5 py-3 font-semibold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                                onClick={() => {
                                    window.location.href =
                                        "mailto:contact@zabehaty.com";
                                }}
                            >
                                {t("send-email")}
                            </button>
                        </div>
                        <div className="relative h-64 flex-1 lg:h-auto">
                            <iframe
                                title="Zabehaty Location"
                                src="https://maps.google.com/maps?q=Abu%20Dhabi&t=&z=13&ie=UTF8&iwloc=&output=embed"
                                className="h-full w-full"
                                allowFullScreen
                                loading="lazy"
                            />
                        </div>
                    </div>
                </section> */}

                <section
                    className="relative mb-36 mt-24 flex items-center py-12"
                    style={{
                        minHeight: "350px",
                        padding: "2rem",
                        width: "100%",
                        backgroundImage: `url('/store/address_map.png')`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent"></div>
                    <div className="relative z-10 flex flex-col justify-center p-6 text-black lg:w-1/3">
                        <h2 className="mb-6 text-3xl">
                            <p className="font-regular">
                                {t("our-location").split(" ")[0]}{" "}
                                <span className="font-bold">
                                    {t("our-location")
                                        .split(" ")
                                        .slice(1)
                                        .join(" ")}
                                </span>
                            </p>
                        </h2>
                        <p className="text-lg font-semibold">{t("address")}:</p>
                        <p>
                            Abu Dhabi City – Al Salam Street “Al Firdous Tower”
                            <br />
                            Abu Dhabi, UAE
                        </p>

                        <button
                            className="group relative mt-4 w-36 overflow-hidden bg-[#01E784] px-4 py-3 text-sm 
                            font-medium text-white transition-colors duration-300"
                            onClick={() => {
                                window.location.href =
                                    "mailto:contact@zabehaty.com";
                            }}
                        >
                            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#01E784]">
                                {t("send-email")}
                            </span>
                            <span
                                className="absolute left-1/2 top-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 
                                rounded-full bg-black transition-all duration-500 ease-out 
                                group-hover:h-40 group-hover:w-40"
                            ></span>
                        </button>
                    </div>
                </section>

                {/* Why Zabehaty */}
                <section className="mx-auto mb-24 max-w-5xl bg-white px-6 py-8">
                    <div className="flex flex-col items-center gap-8 lg:flex-row">
                        <img
                            src="/store/store_logos/logo1.svg"
                            alt="Zabehaty Logo"
                            className="h-auto w-full max-w-xs object-contain lg:max-w-xs"
                        />
                        <div className="text-left">
                            <h2 className="mb-6 text-3xl">
                                <p className="font-regular">
                                    {t("why-zabehaty").split(" ")[0]}{" "}
                                    <span className="font-bold">
                                        {t("why-zabehaty")
                                            .split(" ")
                                            .slice(1)
                                            .join(" ")}
                                    </span>
                                </p>
                            </h2>
                            <p className="text-gray-700">
                                {t("why-zabehaty-desc")}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Contact Us */}
                <section className="mx-auto max-w-4xl px-6 pb-10 pt-2">
                    <h2 className="mb-6 text-center text-3xl">
                        <p className="font-regular">
                            <span className="font-bold">
                                {t("contact-us").split(" ")[0]}{" "}
                            </span>
                            {t("contact-us").split(" ").slice(1).join(" ")}
                        </p>
                    </h2>
                    <form
                        className="flex flex-col gap-6"
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <input
                            type="text"
                            placeholder={t("your-name")}
                            className="border border-[#e8e8e8] bg-[#fafafa] px-4 py-3"
                        />
                        <input
                            type="email"
                            placeholder={t("your-email")}
                            className="border border-[#e8e8e8] bg-[#fafafa] px-4 py-3"
                        />
                        <input
                            type="text"
                            placeholder={t("mobile-number")}
                            className="border border-[#e8e8e8] bg-[#fafafa] px-4 py-3"
                        />
                        <textarea
                            placeholder={t("message")}
                            rows={4}
                            className="resize-none border border-[#e8e8e8] bg-[#fafafa] px-4 py-3"
                        />

                        <button
                            className="group relative mt-4 w-36 self-center overflow-hidden bg-[#01E784] px-4 py-3 text-center text-sm 
                            font-medium text-white transition-colors duration-300"
                        >
                            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#01E784]">
                                {t("send")}
                            </span>
                            <span
                                className="absolute left-1/2 top-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 
                                rounded-full bg-black transition-all duration-500 ease-out 
                                group-hover:h-40 group-hover:w-40"
                            ></span>
                        </button>
                    </form>
                </section>

                {/* FAQ */}
                <section className="mx-auto my-20 max-w-4xl px-6 pb-10 pt-2">
                    <h3 className="mb-6 text-center text-3xl font-bold">
                        {t("faqs")}
                    </h3>
                    <h5 className="mx-auto mb-6 w-[80%] text-center text-base text-lg">
                        {t("faqs-desc")}
                    </h5>
                    <div className="space-y-4">
                        {i18n.language === "ar" &&
                            faqs_ar.map(({ question, answer }, i) => (
                                <div
                                    key={i}
                                    className="rounded border-b border-gray-200"
                                >
                                    <button
                                        onClick={() =>
                                            setOpenFAQ(openFAQ === i ? null : i)
                                        }
                                        className="flex w-full items-center justify-between px-4 py-3 text-left focus:outline-none"
                                    >
                                        <span>{question}</span>
                                        <span>{openFAQ === i ? "-" : "+"}</span>
                                    </button>
                                    {openFAQ === i && (
                                        <div className="px-4 py-3 text-gray-700">
                                            {answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                        {i18n.language === "en" &&
                            faqs.map(({ question, answer }, i) => (
                                <div
                                    key={i}
                                    className="rounded border-b border-gray-200"
                                >
                                    <button
                                        onClick={() =>
                                            setOpenFAQ(openFAQ === i ? null : i)
                                        }
                                        className="flex w-full items-center justify-between px-4 py-3 text-left focus:outline-none"
                                    >
                                        <span>{question}</span>
                                        <span>{openFAQ === i ? "-" : "+"}</span>
                                    </button>
                                    {openFAQ === i && (
                                        <div className="px-4 py-3 text-gray-700">
                                            {answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                        {i18n.language === "ur" &&
                            faqs_ur.map(({ question, answer }, i) => (
                                <div
                                    key={i}
                                    className="rounded border-b border-gray-200"
                                >
                                    <button
                                        onClick={() =>
                                            setOpenFAQ(openFAQ === i ? null : i)
                                        }
                                        className="flex w-full items-center justify-between px-4 py-3 text-left focus:outline-none"
                                    >
                                        <span>{question}</span>
                                        <span>{openFAQ === i ? "-" : "+"}</span>
                                    </button>
                                    {openFAQ === i && (
                                        <div className="px-4 py-3 text-gray-700">
                                            {answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                </section>

                {/* Footer */}
                <StoreFooter />
            </div>
        </>
    );
}
